import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Bot,
  Clock,
  CheckCircle,
  AlertCircle,
  Settings,
  Zap,
} from 'lucide-react';

interface AgentDispatchConfig {
  id: string;
  auto_accept_threshold: number;
  emergency_routing_enabled: boolean;
  updated_at: string;
}

interface AgentDispatchRecord {
  id: string;
  ticket_id?: string;
  client_name?: string;
  vehicle_name: string;
  driver_name?: string;
  score: number;
  auto_accepted: boolean;
  status: 'suggested' | 'accepted' | 'rejected';
  created_at: string;
}

interface AgentDispatchStats {
  auto_dispatched_today: number;
  avg_response_minutes: number;
  acceptance_rate_pct: number;
  pending_suggestions: number;
  recent: AgentDispatchRecord[];
}

const MOCK_CONFIG: AgentDispatchConfig = {
  id: 'agent_dispatch_config',
  auto_accept_threshold: 75,
  emergency_routing_enabled: true,
  updated_at: new Date().toISOString(),
};

const MOCK_STATS: AgentDispatchStats = {
  auto_dispatched_today: 8,
  avg_response_minutes: 4,
  acceptance_rate_pct: 92,
  pending_suggestions: 2,
  recent: [
    {
      id: '1',
      ticket_id: 'TKT-4421',
      client_name: 'Acme Corp',
      vehicle_name: 'Ford Transit #1',
      driver_name: 'Alex Rivera',
      score: 88,
      auto_accepted: true,
      status: 'accepted',
      created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    },
    {
      id: '2',
      ticket_id: 'TKT-4418',
      client_name: 'TechStart Inc',
      vehicle_name: 'Ram ProMaster #3',
      driver_name: 'Jamie Chen',
      score: 79,
      auto_accepted: true,
      status: 'accepted',
      created_at: new Date(Date.now() - 55 * 60 * 1000).toISOString(),
    },
    {
      id: '3',
      ticket_id: 'TKT-4410',
      client_name: 'GlobalDev LLC',
      vehicle_name: 'Chevy Express #2',
      driver_name: 'Morgan Lee',
      score: 61,
      auto_accepted: false,
      status: 'suggested',
      created_at: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
    },
  ],
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

export default function AgentDispatchPage() {
  const { data: configData, isLoading: configLoading, refetch: refetchConfig } = useApi<AgentDispatchConfig>('/api/agent-dispatch/config');
  const { data: statsData, isLoading: statsLoading } = useApi<AgentDispatchStats>('/api/agent-dispatch/stats');

  const config = configData ?? MOCK_CONFIG;
  const stats = statsData ?? MOCK_STATS;

  const [threshold, setThreshold] = useState<number | null>(null);
  const [emergencyRouting, setEmergencyRouting] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const effectiveThreshold = threshold ?? config.auto_accept_threshold;
  const effectiveEmergency = emergencyRouting ?? config.emergency_routing_enabled;

  const isDirty = threshold !== null || emergencyRouting !== null;

  async function saveConfig() {
    setSaving(true);
    try {
      await fetch('/api/agent-dispatch/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_accept_threshold: effectiveThreshold,
          emergency_routing_enabled: effectiveEmergency,
        }),
      });
      refetchConfig();
      setThreshold(null);
      setEmergencyRouting(null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agent Dispatch"
        description="Automated dispatch via The One Agents — AI-driven vehicle assignment"
        actions={(configLoading || statsLoading) ? <Spinner size={16} /> : null}
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Bot}
          label="Auto-Dispatched Today"
          value={stats.auto_dispatched_today}
          accent="success"
        />
        <StatCard
          icon={Clock}
          label="Avg Response Time"
          value={`${stats.avg_response_minutes} min`}
        />
        <StatCard
          icon={CheckCircle}
          label="Acceptance Rate"
          value={`${stats.acceptance_rate_pct}%`}
          accent={stats.acceptance_rate_pct >= 80 ? 'success' : 'warning'}
        />
        <StatCard
          icon={AlertCircle}
          label="Pending Suggestions"
          value={stats.pending_suggestions}
          accent={stats.pending_suggestions > 0 ? 'warning' : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Config Panel */}
        <Card style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-5">
              <Settings className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                Automation Config
              </h2>
            </div>

            <div className="space-y-5">
              {/* Auto-accept threshold */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                    Auto-Accept Score Threshold
                  </label>
                  <span
                    className="text-sm font-mono font-semibold px-2 py-0.5 rounded"
                    style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}
                  >
                    {effectiveThreshold}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={effectiveThreshold}
                  onChange={e => setThreshold(Number(e.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor: '#3B82F6' }}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Auto all</span>
                  <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Manual only</span>
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                  Suggestions scoring {effectiveThreshold}+ are automatically accepted
                </p>
              </div>

              {/* Emergency routing toggle */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                      Emergency Routing
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                      Bypass threshold for emergency priority
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEmergencyRouting(!effectiveEmergency)}
                    className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors"
                    style={{
                      background: effectiveEmergency ? '#10B981' : 'var(--glass-border)',
                    }}
                    aria-pressed={effectiveEmergency}
                  >
                    <span
                      className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform transition-transform"
                      style={{ transform: effectiveEmergency ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </button>
                </div>
              </div>

              {/* Save button */}
              {isDirty && (
                <button
                  type="button"
                  onClick={saveConfig}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
                  style={{ background: '#3B82F6', color: '#fff' }}
                >
                  <Zap className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save Config'}
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Auto-Dispatches Table */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
                <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                  Recent Auto-Dispatches
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      {['Ticket', 'Client', 'Vehicle / Driver', 'Score', 'Method', 'Status', 'When'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                          No auto-dispatches yet today
                        </td>
                      </tr>
                    ) : (
                      stats.recent.map(record => {
                        const scoreColor = record.score >= 80
                          ? { bg: 'rgba(16,185,129,0.15)', text: '#10B981' }
                          : record.score >= 60
                            ? { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' }
                            : { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF' };

                        const statusColor = record.status === 'accepted'
                          ? { bg: 'rgba(16,185,129,0.15)', text: '#10B981' }
                          : record.status === 'rejected'
                            ? { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' }
                            : { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' };

                        return (
                          <tr key={record.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                            <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--muted-foreground)' }}>
                              {record.ticket_id ?? '—'}
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: 'var(--foreground)' }}>
                              {record.client_name ?? '—'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm" style={{ color: 'var(--foreground)' }}>{record.vehicle_name}</div>
                              {record.driver_name && (
                                <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                                  {record.driver_name}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold font-mono"
                                style={{ background: scoreColor.bg, color: scoreColor.text }}
                              >
                                {record.score}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {record.auto_accepted ? (
                                <span className="flex items-center gap-1 text-xs" style={{ color: '#3B82F6' }}>
                                  <Bot className="w-3 h-3" />
                                  Auto
                                </span>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                  Manual
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                                style={{ background: statusColor.bg, color: statusColor.text }}
                              >
                                {record.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                              {formatRelativeTime(record.created_at)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
